/**
 * The contributor's own status pill for the My Timesheet period bar, plus the
 * `UiStatus` vocabulary it renders and the API-status adapter that produces it.
 *
 * Deliberately *not* `components/StatusPill.tsx`. That component names a
 * sheet's state across the full `draft | submitted | approved | returned |
 * missing` union with approver-side wording and `inbox-pill--*` styling; this
 * one names what the contributor sees on their own sheet, where an
 * un-submitted week reads "Open" (not "Draft") and `missing` cannot occur
 * because the page always has a timesheet in hand. Collapsing the two would
 * turn `status-pill open` / "Open" into `inbox-pill--draft` / "Draft" — a
 * user-visible copy and styling change that the outstanding-nudge plan
 * explicitly defers to follow-up work.
 *
 * Moved here verbatim from `pages/Timesheet.tsx` so both that page and
 * `components/PeriodBar.tsx` can import `UiStatus` / `apiStatusToUi` without a
 * circular import between the page and the component.
 */

export type UiStatus = 'open' | 'returned' | 'submitted' | 'approved';

export function apiStatusToUi(status: string): UiStatus {
  if (status === 'submitted') return 'submitted';
  if (status === 'approved') return 'approved';
  if (status === 'returned') return 'returned';
  return 'open';
}

export function PeriodStatusPill({ status }: { status: UiStatus }) {
  const labels: Record<UiStatus, string> = {
    open: 'Open',
    returned: 'Returned',
    submitted: 'Submitted',
    approved: 'Approved',
  };
  return (
    <span className={`status-pill ${status}`}>
      <span className="dot" aria-hidden="true" />
      <span className="sr-only">Status: </span>
      {labels[status]}
    </span>
  );
}
